import React, {useEffect, useState} from 'react';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import ButtonAtm from "../../../../atomic/Button/Button";
import {useTranslation} from "react-i18next";
import {BASE_API} from "../../../../../constants/api.constants";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {CircularProgress, Grid, Box, Chip, Paper} from "@material-ui/core";
import {getAppLang, getUserToken, isDefined} from "../../../../../utils";
import {useDispatch, useSelector} from "react-redux";
import {Alert} from "@material-ui/lab";
import {
    getCpaClearState,
    getCpaDailyPrice, getPropertyCpa,
    setSyncCpaCompetitor,
    setSyncCpaCompetitorClearState
} from "../../../../../redux/actions/Network/Inventory/CompetitorActions";

const styles = theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(3),
        background: '#0286ff',
        color: '#fff',
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: '#fff',
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
        }
    },
});

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            borderRadius: 16,
            overflow: 'hidden',
            maxWidth: 600,
            width: '100%',
        }
    },
    dialogContent: {
        padding: theme.spacing(3),
        backgroundColor: '#fafafa',
    },
    headerIcon: {
        fontSize: 28,
        marginRight: 12,
        opacity: 0.9,
    },
    headerTitle: {
        fontWeight: 700,
        fontSize: 20,
    },
    headerSubtitle: {
        fontSize: 13,
        opacity: 0.85,
        marginTop: 4,
    },
    infoAlert: {
        borderRadius: 12,
        marginBottom: 20,
        backgroundColor: '#e3f2fd',
        border: '1px solid #90caf9',
        '& .MuiAlert-icon': {
            color: '#1976d2',
        },
        '& .MuiAlert-message': {
            color: '#1565c0',
            fontWeight: 500,
        }
    },
    successAlert: {
        borderRadius: 12,
        marginBottom: 16,
        backgroundColor: '#e8f5e9',
        border: '1px solid #a5d6a7',
    },
    errorAlert: {
        borderRadius: 12,
        marginBottom: 16,
        backgroundColor: '#ffebee',
        border: '1px solid #ef9a9a',
    },
    autocompleteWrapper: {
        marginBottom: 24,
    },
    autocomplete: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#fff',
            transition: 'all 0.2s ease',
            '&:hover': {
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            },
            '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(103, 126, 234, 0.2)',
            },
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e0e0e0',
        },
        '& .MuiInputLabel-outlined': {
            fontWeight: 500,
        },
    },
    chip: {
        margin: 2,
        borderRadius: 8,
        backgroundColor: '#667eea',
        color: '#444',
        fontWeight: 500,
        '& .MuiChip-deleteIcon': {
            color: 'rgba(0, 0, 0, 0.7)',
            '&:hover': {
                color: '#616161',
                background: '#e1e1e191',
                borderRadius: '40px'
            }
        }
    },
    hotelOption: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 4px',
        '&:hover': {
            backgroundColor: '#f5f5f5',
        }
    },
    hotelIcon: {
        width: 40,
        height: 40,
        borderRadius: 8,
        backgroundColor: '#e8eaf6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
        color: '#667eea',
    },
    hotelName: {
        fontWeight: 600,
        color: '#333',
        fontSize: 14,
    },
    hotelLocation: {
        fontSize: 12,
        color: '#888',
        marginTop: 2,
    },
    submitButton: {
        borderRadius: 12,
        padding: '14px 32px',
        fontWeight: 700,
        fontSize: 15,
        textTransform: 'none',
        boxShadow: '0 4px 12px rgba(215, 31, 38, 0.3)',
        transition: 'all 0.2s ease',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 16px rgba(215, 31, 38, 0.4)',
        },
        '&:disabled': {
            backgroundColor: '#ccc',
            boxShadow: 'none',
        }
    },
    selectedCount: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderRadius: 12,
        marginBottom: 16,
        border: '1px solid #e0e0e0',
    },
    countBadge: {
        backgroundColor: '#667eea',
        color: '#fff',
        borderRadius: 20,
        padding: '4px 12px',
        fontWeight: 700,
        fontSize: 13,
    },
    maxBadge: {
        backgroundColor: '#f5f5f5',
        color: '#666',
        borderRadius: 20,
        padding: '4px 12px',
        fontWeight: 500,
        fontSize: 13,
    },
    addButton: {
        borderRadius: 12,
        padding: '12px 24px',
        fontWeight: 600,
        textTransform: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }
    },
    emptyState: {
        textAlign: 'center',
        padding: '24px 16px',
        color: '#888',
    },
    emptyIcon: {
        fontSize: 48,
        marginBottom: 12,
        opacity: 0.5,
    },
}));

const DialogTitle = withStyles(styles)(props => {
    const { children, classes, onClose, subtitle, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Box display="flex" alignItems="center">
                <i className={`fas fa-hotel ${classes.headerIcon}`} style={{fontSize: 28, marginRight: 12}} />
                <Box>
                    <Typography variant="h6" className={classes.headerTitle} style={{fontWeight: 700, fontSize: 20, color: "#fff"}}>
                        {children}
                    </Typography>
                    {subtitle && (
                        <Typography className={classes.headerSubtitle} style={{fontSize: 13, opacity: 0.85, marginTop: 4}}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles(theme => ({
    root: {
        padding: theme.spacing(3),
        backgroundColor: '#fafafa',
    },
}))(MuiDialogContent);


const UpdateOrCreateCompetitorAnalysisModal = ({ allDays, getCpa, type, onHotelAdded, currency, propertyProducts }) =>  {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const classes = useStyles();

    const { syncCompetitor, errorSyncCompetitor } = useSelector(props => props?.cpaReducer);

    const filteredCpa = (getCpa || []).filter(item => item.competitor_property_source !== 'extranetwork');

    const [isOpen, setIsOpen] = useState(false);
    const [data, setData] = useState(getCpa);
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const loading = open && options.length === 0 && isSearching;
    const lsAllDays = JSON.parse(localStorage.getItem("allDays"));

    const competitorProduct = propertyProducts?.get_products?.data ?
        Object.values(propertyProducts.get_products.data).find(item => item?.code === "CMP") : null;

    const param700 = competitorProduct?.parameters?.find(p => p?.product_parameter_id === 700 && p?.is_selected === 1);
    const maxPropertyCount = param700 ? parseInt(param700.value, 10) : (competitorProduct?.parameterArray?.maxPropertyCount ?? 10);

    const handleClickOpen = () => {
        setIsOpen(true);
    };
    const handleClose = () => {
        setIsOpen(false);
    };

    const onChangeHandle = async value => {
        if (value.length >= 3 && data.length < maxPropertyCount) {
            setIsSearching(true);
            try {
                const response = await fetch(BASE_API + "/cpa/property",
                    {
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'authToken': getUserToken(),
                            'language' : getAppLang()
                        },
                        method: "POST",
                        body: JSON.stringify({keyword: value})
                    })

                const result = await response.json();
                let resultData = [];
                if (result?.data && Array.isArray(result.data)) {
                    resultData = result.data;
                } else if (result?.data) {
                    resultData = Object.keys(result.data).map(key => result.data[key]);
                }
                // Filter out extranetwork sources from search results
                resultData = resultData.filter(item => item.competitor_property_source !== 'extranetwork');
                setOptions(resultData);
            } finally {
                setIsSearching(false);
            }
        }
    };

    const selectedHotels = (selectedData) => {
        setData(selectedData)
    };

    useEffect(() => {
        const filtered = (getCpa || []).filter(item => item.competitor_property_source !== 'extranetwork');
        setData(filtered);
    }, [getCpa]);

    useEffect(() => {
        if (!open) {
        }
    }, [open]);

    const onSubmit = (e) => {
        e.preventDefault();

        if (!data || data.length === 0) return;

        const extranetworkCompetitors = (getCpa || []).filter(
            item => item.competitor_property_source === 'extranetwork'
        );

        const allCompetitors = [...data, ...extranetworkCompetitors];

        dispatch(setSyncCpaCompetitor(allCompetitors));
        if (typeof onHotelAdded === 'function') {
            onHotelAdded();
        }
    };

    useEffect( () => {
        if (syncCompetitor.status === 200) {
            window.stop();

            setTimeout(() => {
                dispatch(getPropertyCpa());

                lsAllDays.map((day) => {
                    data.map((cpa) => {
                        dispatch(getCpaDailyPrice(day, cpa?.key, currency))
                    });
                });
            }, 100)

            setIsOpen(false);
        }
    },[allDays, dispatch, getCpa, syncCompetitor])

    useEffect( () => {
        if (isOpen === false) {
            dispatch(setSyncCpaCompetitorClearState());
            dispatch(getCpaClearState());
        }
    },[dispatch, isOpen])

    const isMaxReached = data?.length >= maxPropertyCount;

    return (
        <>
            <ButtonAtm
                className={type === "edit" ? 'inventory-action-btn bg-dark-blue inventory-filter-box' : "filter-action-button cpa-new-property-btn"}
                buttontext={t('enw-cpa_add-hotel')}
                fullwidth={false}
                onClick={handleClickOpen}
                startIcon={<i style={{fontSize: "12px", marginTop: "2px"}} className="fas fa-plus" />}
            />
            <Dialog 
                onClose={handleClose} 
                disableBackdropClick={true} 
                aria-labelledby="rate-plan" 
                open={isOpen}
                className={classes.dialog}
            >
                <DialogTitle 
                    id="rate-plan" 
                    onClose={handleClose}
                    // subtitle={t('enw-cpa_add-hotel-modal-subtitle')}
                >
                    {t('enw-cpa_add-hotel-modal-title')}
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    {syncCompetitor.status === 200 && (
                        <Alert severity="success" className={classes.successAlert}>
                            {t('enw-cpa_add-hotel-success-message')}
                        </Alert>
                    )}
                    {isDefined(errorSyncCompetitor?.response) && (
                        <Alert severity="error" className={classes.errorAlert}>
                            {errorSyncCompetitor?.response?.data?.message}
                        </Alert>
                    )}

                    {/* Selected Count Box */}
                    <Box className={classes.selectedCount}>
                        <Box display="flex" alignItems="center">
                            <i className="fas fa-building" style={{ color: '#667eea', marginRight: 8 }} />
                            <Typography style={{ fontWeight: 500, color: '#444' }}>
                                {t('enw-cpa-selected_hotels')}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <span className={classes.countBadge}>{data?.length || 0}</span>
                            <span style={{ color: '#999' }}>/</span>
                            <span className={classes.maxBadge}>{maxPropertyCount}</span>
                        </Box>
                    </Box>

                    <Alert severity="info" className={classes.infoAlert} icon={<i className="fas fa-info-circle" />}>
                        {t('enw-cpa_add-hotel-info-message', {length: maxPropertyCount})}
                    </Alert>

                    <form onSubmit={onSubmit}>
                        <Grid container>
                            <Grid item xs={12} className={classes.autocompleteWrapper}>
                                <Autocomplete
                                    id="add-hotel"
                                    filterOptions={(x) => x}
                                    open={open}
                                    onOpen={() => setOpen(true)}
                                    onClose={() => setOpen(false)}
                                    options={options}
                                    getOptionSelected={(option, value) => option.key === value.key}
                                    getOptionLabel={option => isDefined(option.location) ? option?.name + " (" + option.location + ")" : option?.name}
                                    onChange={(event, value) => selectedHotels(value)}
                                    multiple
                                    filterSelectedOptions
                                    defaultValue={filteredCpa}
                                    loading={loading}
                                    // disabled={isMaxReached}
                                    className={classes.autocomplete}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                label={option?.name}
                                                {...getTagProps({ index })}
                                                className={classes.chip}
                                                deleteIcon={<CloseIcon style={{ fontSize: 16 }} />}
                                            />
                                        ))
                                    }
                                    renderOption={(option) => (
                                        <Box className={classes.hotelOption}>
                                            <Box className={classes.hotelIcon}>
                                                <i className="fas fa-hotel" />
                                            </Box>
                                            <Box>
                                                <Typography className={classes.hotelName}>
                                                    {option?.name}
                                                </Typography>
                                                {option?.location && (
                                                    <Typography className={classes.hotelLocation}>
                                                        <i className="fas fa-map-marker-alt" style={{ marginRight: 4, fontSize: 10 }} />
                                                        {option.location}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                    PaperComponent={({ children, ...props }) => (
                                        <Paper {...props} style={{ borderRadius: 12, marginTop: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                                            {children}
                                        </Paper>
                                    )}
                                    noOptionsText={
                                        <Box className={classes.emptyState}>
                                            <i className={`fas fa-search ${classes.emptyIcon}`} />
                                            <Typography>{t('enw-notification-no_results')}</Typography>
                                        </Box>
                                    }
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label={t('enw-cpa_add-hotel-label')}
                                            variant="outlined"
                                            onChange={ev => {
                                                if (ev.target.value !== "" || ev.target.value !== null) {
                                                    onChangeHandle(ev.target.value);
                                                }
                                            }}
                                            inputProps={{
                                                ...params.inputProps,
                                                disabled: isMaxReached,
                                            }}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <i className="fas fa-search" style={{ color: '#999', marginRight: 8, marginLeft: 4 }} />
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                ),
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                )
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} style={{ textAlign: 'center' }}>
                                <ButtonAtm
                                    type="submit"
                                    className={`room-rate-modal-save room-rate-save bg-red m-auto ${classes.submitButton}`}
                                    buttontext={t('btn-save')}
                                    disabled={!data || data.length === 0}
                                    startIcon={<i className="fas fa-check" style={{ marginRight: 4 }} />}
                                />
                            </Grid>
                        </Grid>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default UpdateOrCreateCompetitorAnalysisModal;
